// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A search component for selecting event types from available configurations.
 * This module provides an autocomplete interface for choosing event types,
 * with support for form integration and remote data fetching. The component
 * filters out event types that require event groups for simpler selection.
 *
 * Key features:
 * - Remote data fetching
 * - Form integration
 * - Error handling
 * - Loading states
 * - Event filtering
 * - Ref forwarding
 * - Type safety
 */
import { getEventConfigurations } from '@/asset/api/get-event-configurations';
import { EventConfiguration } from '@/asset/api/types';
import { EventAutocomplete } from '@/asset/components/event-autocomplete';
import { useQuery } from '@tanstack/react-query';
import { ForwardedRef, forwardRef } from 'react';

/**
 * Props for configuring the EventSearch component.
 * @typedef {Object} Props
 */
interface Props {
  /**
   * The name of the form input element.
   * Used for form integration and accessibility.
   *
   * @example "eventType"
   */
  name?: string;

  /**
   * The label displayed above the input.
   * Provides context for the field's purpose.
   *
   * @example "Select event type"
   */
  label?: string;

  /**
   * Whether the component is in an error state.
   * When true, displays red border and error styling.
   *
   * @default false
   */
  error?: boolean;

  /**
   * Text displayed below the input.
   * Used for additional guidance or error messages.
   *
   * @example "Please select a valid event type"
   */
  helperText?: string;

  /**
   * The currently selected event configuration.
   * Null when no event type is selected.
   *
   * @example
   * ```ts
   * const value = {
   *   id: 'event-123',
   *   type: {
   *     id: 'type-456',
   *     name: 'Maintenance Check',
   *     requiresEventGroup: false
   *   }
   * };
   * ```
   */
  value: EventConfiguration | null;

  /**
   * Callback fired when selection changes.
   * Receives the newly selected event configuration
   * or null when selection is cleared.
   *
   * @param value - The new selection or null
   *
   * @example
   * ```ts
   * const handleChange = (config: EventConfiguration | null) => {
   *   console.log('Selected:', config?.type.name);
   * };
   * ```
   */
  onChange: (value: EventConfiguration | null) => void;
}

/**
 * Internal renderer for the event search component.
 * Handles data fetching and selection logic while forwarding
 * the ref to the underlying autocomplete component.
 *
 * Features:
 * - Automatic data fetching with react-query
 * - Loading state management
 * - Event type filtering
 * - Selection change handling
 * - Ref forwarding to input
 *
 * @param props - Component configuration options
 * @param ref - Forwarded ref for the input element
 */
function EventSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { data, isLoading } = useQuery({
    queryKey: ['event-configuration'],
    queryFn: getEventConfigurations,
  });

  // Filter out event types that require groups
  const options =
    data?.eventConfigurations.filter(
      (configuration) => !configuration.type.requiresEventGroup
    ) ?? [];

  const handleChange = (_: unknown, value: EventConfiguration | null) => {
    props.onChange(value);
  };

  return (
    <EventAutocomplete
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      options={options}
      loading={isLoading}
      onChange={handleChange}
      helperText={props.helperText}
      loadingText="Loading..."
    />
  );
}

/**
 * A controlled component for selecting event types.
 * Provides a searchable dropdown interface for choosing
 * from available event configurations, with support for
 * form integration and error handling.
 *
 * Features:
 * - Remote data fetching
 * - Event type filtering
 * - Form integration
 * - Error handling
 * - Loading states
 * - Keyboard navigation
 * - Screen reader support
 *
 * Usage example:
 * ```tsx
 * const [selected, setSelected] = useState<EventConfiguration | null>(null);
 *
 * return (
 *   <EventSearch
 *     label="Select event type"
 *     value={selected}
 *     onChange={setSelected}
 *     helperText="Choose an event type to continue"
 *   />
 * );
 * ```
 */
export const EventSearch = forwardRef(EventSearchRenderer);
